// Wrappers de fetch para la API de I-DO-YOGA.
// API_BASE viene de REACT_APP_API_URL (build-time, CRA inyecta en build).
// En dev (sin .env) cae a localhost:3002 para no romper el flujo local.
export const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002';

// Helper: throws ApiError con { status, code, error } cuando no-OK.
export class ApiError extends Error {
  constructor(status, code, mensaje) {
    super(mensaje || `HTTP ${status}`);
    this.status = status;
    this.code = code;
  }
}

async function jsonOrThrow(res) {
  let body = null;
  try { body = await res.json(); } catch (_) { /* puede no haber body */ }
  if (!res.ok) {
    throw new ApiError(res.status, body?.code, body?.error);
  }
  return body;
}

export function urlHorarios({ desde, hasta, idioma, estilo, formato, nivel }) {
  const params = new URLSearchParams({ desde, hasta });
  if (idioma) params.set('idioma', idioma);
  if (estilo && estilo.length) params.set('estilo', estilo.join(','));
  if (formato && formato !== 'todos') params.set('formato', formato);
  if (nivel && nivel !== 'todos') params.set('nivel', nivel);
  return `${API_BASE}/api/horarios?${params.toString()}`;
}

export async function fetchHorarios(opts) {
  const r = await fetch(urlHorarios(opts));
  return jsonOrThrow(r);
}

export async function postReserva(payload, token, idioma) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const url = idioma
    ? `${API_BASE}/api/reservas?idioma=${encodeURIComponent(idioma)}`
    : `${API_BASE}/api/reservas`;
  const r = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  return jsonOrThrow(r);
}

export async function getReserva(codigo, email) {
  const params = new URLSearchParams();
  if (email) params.set('email', email);
  const r = await fetch(`${API_BASE}/api/reservas/${encodeURIComponent(codigo)}?${params.toString()}`);
  return jsonOrThrow(r);
}

export async function cancelarReserva(codigo, email, idioma) {
  const params = new URLSearchParams();
  if (email) params.set('email', email);
  if (idioma) params.set('idioma', idioma);
  const r = await fetch(`${API_BASE}/api/reservas/${encodeURIComponent(codigo)}?${params.toString()}`, {
    method: 'DELETE',
  });
  return jsonOrThrow(r);
}

export async function crearCheckoutSession(codigo, email) {
  const r = await fetch(`${API_BASE}/api/reservas/${encodeURIComponent(codigo)}/checkout-session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(email ? { email } : {}),
  });
  return jsonOrThrow(r);
}

// Endpoints autenticados: requieren token Bearer.
export async function getMisReservas(token) {
  const r = await fetch(`${API_BASE}/api/auth/me/reservas`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return jsonOrThrow(r);
}

export async function getMisBonos(token, idioma) {
  const url = idioma
    ? `${API_BASE}/api/auth/me/bonos?idioma=${encodeURIComponent(idioma)}`
    : `${API_BASE}/api/auth/me/bonos`;
  const r = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return jsonOrThrow(r);
}

export async function getBonosTipos(idioma) {
  const url = idioma
    ? `${API_BASE}/api/bonos-tipos?idioma=${encodeURIComponent(idioma)}`
    : `${API_BASE}/api/bonos-tipos`;
  const r = await fetch(url);
  return jsonOrThrow(r);
}

export async function crearCheckoutBono(token, bonoTipoId, codigo) {
  const r = await fetch(`${API_BASE}/api/auth/me/bonos/checkout-session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ bono_tipo_id: bonoTipoId, ...(codigo ? { codigo_descuento: codigo } : {}) }),
  });
  return jsonOrThrow(r);
}

// Eventos (SS-EVT)
export async function getEventos(idioma) {
  const url = idioma
    ? `${API_BASE}/api/eventos?idioma=${encodeURIComponent(idioma)}`
    : `${API_BASE}/api/eventos`;
  const r = await fetch(url);
  return jsonOrThrow(r);
}

export async function getEvento(slug, idioma) {
  const url = idioma
    ? `${API_BASE}/api/eventos/${encodeURIComponent(slug)}?idioma=${encodeURIComponent(idioma)}`
    : `${API_BASE}/api/eventos/${encodeURIComponent(slug)}`;
  const r = await fetch(url);
  return jsonOrThrow(r);
}

export async function inscribirEvento(slug, payload, token) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const r = await fetch(`${API_BASE}/api/eventos/${encodeURIComponent(slug)}/inscribir`, {
    method: 'POST', headers, body: JSON.stringify(payload || {}),
  });
  return jsonOrThrow(r);
}

export async function cancelarInscripcionEvento(inscripcionId, email) {
  const params = new URLSearchParams();
  if (email) params.set('email', email);
  const r = await fetch(`${API_BASE}/api/eventos/inscripciones/${encodeURIComponent(inscripcionId)}?${params.toString()}`, {
    method: 'DELETE',
  });
  return jsonOrThrow(r);
}

// SS7.2.5 — precios profesor (clases + bonos con descuento aplicado).
// Solo válido si el caller es rol='profesor' (backend filtra). Devuelve
// { profesor_id, data: [...clases], bonos: [...] }.
export async function getProfesorPrecios(token) {
  const r = await fetch(`${API_BASE}/api/profesor/precios`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return jsonOrThrow(r);
}

// Códigos de descuento y tarjetas regalo
export async function validarCodigo(payload, token) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const r = await fetch(`${API_BASE}/api/codigos/validar`, { method: 'POST', headers, body: JSON.stringify(payload) });
  return jsonOrThrow(r);
}

export async function crearCheckoutRegalo(payload, token) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const r = await fetch(`${API_BASE}/api/regalos/checkout-session`, { method: 'POST', headers, body: JSON.stringify(payload) });
  return jsonOrThrow(r);
}

// Tarjetas regalo de empresa
const postJSON = async (path, body) => jsonOrThrow(await fetch(`${API_BASE}${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }));
export const getCatalogoEmpresa = async (idioma) => jsonOrThrow(await fetch(`${API_BASE}/api/empresas/catalogo?idioma=${idioma || 'es'}`));
export const calcularPedidoEmpresa = (payload) => postJSON('/api/empresas/calcular', payload);
export const crearCheckoutEmpresa = (payload) => postJSON('/api/empresas/checkout-session', payload);
export const getPedidoEmpresa = async (token) => jsonOrThrow(await fetch(`${API_BASE}/api/empresas/pedido/${encodeURIComponent(token)}`));
export const enviarTarjetasEmpresa = (token, destinatarios) => postJSON(`/api/empresas/pedido/${encodeURIComponent(token)}/enviar`, { destinatarios });

// Clases particulares a solicitud
export async function postSolicitudClase(payload) {
  const r = await fetch(`${API_BASE}/api/solicitudes-clase`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
  return jsonOrThrow(r);
}
export async function getSolicitudClase(token) {
  return jsonOrThrow(await fetch(`${API_BASE}/api/solicitudes-clase/${encodeURIComponent(token)}`));
}
export async function checkoutSolicitudClase(token) {
  return jsonOrThrow(await fetch(`${API_BASE}/api/solicitudes-clase/${encodeURIComponent(token)}/checkout`, { method: 'POST' }));
}
